/* global s */
/* eslint-disable no-console */
'use strict';

class AutoComplete {
	constructor(props = {}) {
		console.log('AutoComplete::constructor(%o)', props);

		this.options = {
			searchInput: document.querySelector('.js-auto-complete-input'),
			suggestionsContainer: document.querySelector('.js-auto-complete-suggestions'),
			countryInputAppend: document.querySelector('.country-input-append')
		};

		Object.assign(this, props);

		this.countries = s.options._auto_complete_v3_options.countries || [];
		this.regions = [];
		for (let region of this.countries) {
			if (region?.is_region) {
				this.regions[region.english_name] = region;
			}
		}
		this.style_color_dir = s.options._auto_complete_v3_options.style_color_dir || 'lightblue-green';
		this.language_id = s.options._auto_complete_v3_options.language_id || 1;

		this.page_timers = {
			search_country: null
		};

		this.pageEvents();
	}

	pageEvents() {
		const _that = this;

		if (_that.options.searchInput && _that.countries.length > 0) {
			_that.options.suggestionsContainer.style.top = `${_that.options.searchInput.offsetHeight + 1}px`;
			_that.options.suggestionsContainer.style.width = `${_that.options.searchInput.offsetWidth}px`;

			_that.options.searchInput.addEventListener('input', (event) => {
				const searchString = event.target.value.trim().toLowerCase();
				const escapeSpecialChars = (str) => str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');

				const highlightSearch = (str, search) => {
					const escapedSearch = escapeSpecialChars(search);
					const regex = new RegExp(`(${escapedSearch})`, "i");
					return str.replace(regex, '<strong style="text-decoration: underline;">$1</strong>');
				};

				if (searchString.length === 0 || !isNaN(searchString) || !/[a-z]/.test(searchString)) {
					_that.options.suggestionsContainer.innerHTML = '';
					return;
				}

				clearTimeout(_that.page_timers.search_country);
				_that.page_timers.search_country = setTimeout(async () => {
					const suggestions = _that.findCountryAndRegionSuggestions(searchString);
					if (suggestions.length === 0) {
						_that.options.suggestionsContainer.innerHTML =
							`<span class="d-block px-15 py-10">${s.options.messages.TRAVEL_ESIM_COUNTRY_NOT_SUPPORTED}</span>`;
						return;
					}

					_that.options.suggestionsContainer.innerHTML = suggestions
						.map((country) => {
							let flagElement;
							const nameElement = `<div class='country-name'>${highlightSearch(country.translated_name, searchString)}</div>`;

							if (country.is_region) {
								flagElement = `<div><img src="${s.url(`/images/icons/theme/${_that.style_color_dir}/maps/${country.code}.png`)}" alt="Region flag" width="40"></div>`;
							} else {
								flagElement = `<div class="icon-region-flag" data-flag="country-${country.code}"></div>`;
							}

							return _that.generateCountrySuggestionLink(country, flagElement, nameElement);
						})
						.join('');
				}, 300);
			});

			_that.options.searchInput.addEventListener('keydown', (event) => {
				const key = event.key;
				const container = _that.options.suggestionsContainer;
				const input = _that.options.searchInput;

				if (key === 'ArrowDown' || key === 'ArrowUp') {
					event.preventDefault();
					const items = container.querySelectorAll('li');
					const selected = container.querySelector('li.selected');
					let index = selected ? Array.from(items).indexOf(selected) : -1;

					if (key === 'ArrowDown') index++;
					else if (key === 'ArrowUp') index--;

					if (index >= 0 && index < items.length) {
						if (selected) selected.classList.remove('selected');
						items[index].classList.add('selected');
						input.value = items[index].querySelector('.country-name').textContent;

						// Scroll the selected item into view
						items[index].scrollIntoView({
							behavior: 'smooth',
							block: 'nearest',
						});
					}
				}
				else if (key === 'Escape') {
					container.innerHTML = '';
				}
				else if (key === 'Enter') {
					event.preventDefault();
					const selected = container.querySelector('li.selected');
					if (selected) {
						selected.parentElement.click();
					} else {
						const match = Array.from(container.querySelectorAll('li .country-name')).find(
							el => el.textContent.toLowerCase() === input.value.toLowerCase()
						);

						if (match && match.parentElement.parentElement.tagName === 'A') {
							match.parentElement.parentElement.click();
						}
					}
				}
			});
		}

		if (_that.options.countryInputAppend && _that.options.searchInput) {
			_that.options.countryInputAppend.addEventListener('click', () => {
				_that.options.searchInput.focus();
			});
		}
	}

	/**
	 * Finds and ranks country and region suggestions based on a user-provided search string.
	 * Uses fuzzy matching, language preference, and string similarity to return relevant results.
	 * Filters out unavailable or duplicate entries and orders results by relevance.
	 *
	 * @param {string} searchString - The user input to search for countries or regions.
	 * @returns {Array}
	 */
	findCountryAndRegionSuggestions(searchString) {
		const normalizedSearch = searchString
			.normalize("NFD")
			.replace(/[\u0300-\u036f]/g, "") // Remove diacritical marks (e.g. "e" + "´" becomes just "e")
			.replace(/\./g, '') // Remove dots
			.replace(/,/g, '') // Remove commas
			.replace(/\s+/g, '') // Remove all whitespace
			.toLowerCase();

		// Early filtering: exclude unavailable countries and regions
		const availableCountries = this.countries.filter(
			country => country.is_available !== '0'
		);

		let countrySuggestions = [];
		let regionSuggestions = [];
		let suggestionsWithOtherLanguage = [];

		const seenCodes = new Set(); // Track unique country/region codes

		for (let countryItem of availableCountries) {
			// Skip if the code has already been added
			if (seenCodes.has(countryItem.code)) {
				continue;
			}

			const normalizedName = countryItem.translated_name
				.normalize("NFD") // Unicode Normalization Form D (e.g. "é" becomes "e" + "´")
				.replace(/[\u0300-\u036f]/g, "") // Remove diacritical marks (e.g. "e" + "´" becomes just "e")
				.replace(/\./g, '') // Remove dots
				.replace(/,/g, '') // Remove commas
				.replace(/\s+/g, '') // Remove all whitespace
				.toLowerCase();

			// Calculate Levenshtein distance and base score
			const distance = this.levenshtein(normalizedSearch, normalizedName);
			let score = (distance / Math.max(normalizedName.length, 1)) * 100;

			// Score for matching language
			if (parseInt(countryItem.language_id) === parseInt(this.language_id)) {
				score -= 30; // Bonus for matching language
			} else {
				score += 50; // Penalty for not matching language
			}

			// Perfect match
			if (normalizedSearch === normalizedName) {
				score = -100;
			}
			// Prefix match (e.g. "Con" in "Congo")
			else if (normalizedName.startsWith(normalizedSearch)) {
				score -= 55; // Strong boost for startsWith
			}
			// Substring match (e.g. "con" in "Democratic Republic of Congo")
			else if (normalizedName.includes(normalizedSearch)) {
				// Boost for multi-word matches where the search string is a prefix of any word
				const cleanedName = normalizedName.replace(/[^a-zA-Z0-9 ]/g, '');
				const words = cleanedName.split(' ');
				const isPrefixOfAnyWord = words.some(word => word.startsWith(normalizedSearch));

				// Strong boost for startsWith
				if (isPrefixOfAnyWord) {
					score -= 55;
				}
				// Default substring match
				else {
					score -= 35;
				}

				words.forEach(word => {
					if (normalizedSearch === word && parseInt(countryItem.language_id) === parseInt(this.language_id)) {
						// Perfect match on language (e.g. "congo" in "Democratic Republic of Congo")
						score = -100;
					}
				});
			}
			// No match, use fuzzy logic
			else {
				score += 20; // Penalty for fuzzy matches
			}

			// For other language suggestions keep countries with a higher score
			if (score < 75) {
				if (!countryItem.is_region && parseInt(countryItem.language_id) !== parseInt(this.language_id)) {
					suggestionsWithOtherLanguage.push({ ...countryItem, score });
				}
			}

			// For final suggestions keep countries with a strict score
			// Making this threshold higher will increase the number of countries returned, but will also increase the number of false positives
			if (score <= 25) {
				if (countryItem.is_region) {
					regionSuggestions.push({ ...countryItem, score });
				} else {
					countrySuggestions.push({ ...countryItem, score });
				}
				seenCodes.add(countryItem.code); // Mark this country code as seen
			}
		}

		// Sort countries alphabetically
		countrySuggestions.sort((a, b) => {
			const aStarts = a.translated_name
				.normalize("NFD")
				.replace(/[\u0300-\u036f]/g, "")
				.toLowerCase()
				.startsWith(normalizedSearch);
			const bStarts = b.translated_name
				.normalize("NFD")
				.replace(/[\u0300-\u036f]/g, "")
				.toLowerCase()
				.startsWith(normalizedSearch);

			if (aStarts && !bStarts) return -1;
			if (!aStarts && bStarts) return 1;

			return a.translated_name.localeCompare(b.translated_name);
		});

		// Sort regions by score, then alphabetically
		regionSuggestions.sort((a, b) => {
			if (a.score !== b.score) {
				return a.score - b.score;
			}
			return a.translated_name.localeCompare(b.translated_name);
		});

		// Limit to top 5 countries and top 3 regions
		const limitedCountrySuggestions = countrySuggestions.slice(0, 5);
		const limitedRegionSuggestions = regionSuggestions.slice(0, 3);

		if (limitedCountrySuggestions.length > 0 || limitedRegionSuggestions.length > 0) {
			const countrySuggestionsWithRegions = this.addRegionsToSuggestions(limitedCountrySuggestions);
			// Merge results: countries first, then regions
			const mergedSuggestions = [...countrySuggestionsWithRegions, ...limitedRegionSuggestions];
			return this.processSuggestions(mergedSuggestions);
		} else {
			// Recheck the suggestions with other language
			if (suggestionsWithOtherLanguage.length === 0) {
				return [];
			}
			const countrySuggestionsWithRegions = this.addRegionsToSuggestions(suggestionsWithOtherLanguage);
			return this.processSuggestions(countrySuggestionsWithRegions);
		}
	}

	/**
	 * Adds unique regions (including "Global") to suggestions if they are not already present.
	 * "Global" is always placed at the end of the region list.
	 *
	 * @param {Array} suggestions
	 * @returns {Array}
	 */
	addRegionsToSuggestions(suggestions) {
		if (!suggestions.length) {
			return suggestions;
		}

		// Extract unique regions from all suggestions
		const uniqueRegions = [...new Set(suggestions.flatMap(item => item.regions || []))].filter(Boolean);

		if (!uniqueRegions.length) {
			return suggestions;
		}

		// Separate "Global" from other regions
		const nonGlobalRegions = uniqueRegions.filter(region => region.toLowerCase() !== 'global');
		const globalRegions = uniqueRegions.filter(region => region.toLowerCase() === 'global');

		// Combine regions, ensuring "Global" is last
		const regionsWithGlobalLast = [...nonGlobalRegions, ...globalRegions];

		// Add each region to suggestions if it exists in `this.regions`
		regionsWithGlobalLast.forEach(region => {
			if (this.regions[region] && !suggestions.some(item => item.code === this.regions[region].code)) {
				suggestions.push(this.regions[region]);
			}
		});

		return suggestions;
	}

	/**
	 * Helper function to process and order suggestions
	 *
	 * @param {Array} suggestions
	 * @returns {Array}
	 */
	processSuggestions(suggestions) {
		if (!suggestions.length) {
			return suggestions;
		}

		// Remove duplicates based on the `code` property
		const uniqueSuggestions = suggestions.reduce((acc, current) => {
			const isDuplicate = acc.some(item => item.code === current.code);
			if (!isDuplicate) {
				acc.push(current);
			}
			return acc;
		}, []);

		// Order the final result: countries, regions, "Global" last
		return [
			...uniqueSuggestions.filter(item => !item.is_region && item.code !== "global"),
			...uniqueSuggestions.filter(item => item.is_region && item.code !== "global"),
			...uniqueSuggestions.filter(item => item.code === "global"),
		];
	}

	levenshtein(a, b) {
		if (a.length === 0) return b.length;
		if (b.length === 0) return a.length;

		// Initialize the matrix with (b.length + 1) rows and (a.length + 1) columns
		const matrix = Array(b.length + 1).fill().map(() => Array(a.length + 1).fill(0));

		// Fill the first row and column
		for (let i = 0; i <= b.length; i++) {
			matrix[i][0] = i;
		}
		for (let j = 0; j <= a.length; j++) {
			matrix[0][j] = j;
		}

		// Fill the rest of the matrix
		for (let i = 1; i <= b.length; i++) {
			for (let j = 1; j <= a.length; j++) {
				if (b.charAt(i - 1) === a.charAt(j - 1)) {
					matrix[i][j] = matrix[i - 1][j - 1];
				} else {
					matrix[i][j] = Math.min(
						matrix[i - 1][j - 1] + 1,		// substitution
						matrix[i][j - 1] + 1,				// insertion
						matrix[i - 1][j] + 1				// deletion
					);
				}
			}
		}

		return matrix[b.length][a.length];
	}

	/**
	 * Generates an HTML link for a country suggestion, including its flag and name.
	 * Constructs the link URL based on the search input's dataset, form action, or a default path.
	 *
	 * @param {Object} country
	 * @param {string} flagElement
	 * @param {string} nameElement
	 * @returns {string}
	 */
	generateCountrySuggestionLink(country, flagElement, nameElement) {
		const form = this.options.searchInput.closest('form');
		let linkUrl;

		if (this.options.searchInput.dataset.suggestLink) {
			linkUrl = s.url(this.options.searchInput.dataset.suggestLink + country.english_name);
		} else if (form && form.action) {
			linkUrl = form.action + '/' + country.english_name;
		} else {
			linkUrl = s.url('international-calls/' + country.english_name);
		}

		return `<a href="${linkUrl}"><li>${flagElement} ${nameElement}</li></a>`;
	}
}

window.addEventListener('DOMContentLoaded', () => {
	try {
		new AutoComplete();
	} catch (error) {
		console.error(error);
	}
});
